import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Box } from '@mui/material';
import { LocalizationProvider, DateCalendar } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr } from 'date-fns/locale';
import CustomCalendarHeader from './CustomCalendarHeader';
import RangeDayPicker from './RangeDayPicker';
import chevronDownIcon from '../../assets/icons/chevron-down.svg';
import xIcon from '../../assets/icons/x.svg';

const BLUE_CHEVRON = 'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(188deg) brightness(89%) contrast(101%)';

const DateRangeFilter = ({ dateRange, onChange, placeholder = "Aujourd'hui" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (dateRange?.start && dateRange?.end) {
      setStartDate(new Date(dateRange.start));
      setEndDate(new Date(dateRange.end));
    } else {
      setStartDate(null);
      setEndDate(null);
    }
  }, [dateRange]);

  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
    } else if (!isOpen) {
      setDropdownPosition(null);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  const handleDateSelection = (selected) => {
    if (!startDate) {
      setStartDate(selected);
      setEndDate(null);
    } else if (!endDate) {
      if (selected >= startDate) setEndDate(selected);
      else { setStartDate(selected); setEndDate(null); }
    } else {
      setStartDate(selected);
      setEndDate(null);
    }
  };

  const handleApply = () => {
    if (startDate) onChange({ start: startDate, end: endDate || startDate });
    setIsOpen(false);
  };

  const handleCancel = () => {
    if (dateRange?.start) setStartDate(new Date(dateRange.start)); else setStartDate(null);
    if (dateRange?.end) setEndDate(new Date(dateRange.end)); else setEndDate(null);
    setIsOpen(false);
  };

  const handleReset = () => {
    setStartDate(null);
    setEndDate(null);
    onChange(null);
    setIsOpen(false);
  };

  const getDisplay = () => {
    if (startDate && endDate) {
      const today = new Date();
      if (startDate.toDateString() === today.toDateString() && endDate.toDateString() === today.toDateString()) {
        return "Aujourd'hui";
      }
      const fmt = (d) => `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
      return `${fmt(startDate)} - ${fmt(endDate)}`;
    }
    return placeholder;
  };

  return (
    <div>
      <Box
        style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
      >
        <Box
          ref={triggerRef}
          onClick={() => setIsOpen(o => !o)}
          style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        >
          <span style={{
            fontFamily: 'Montserrat',
            fontWeight: 600,
            fontSize: '14px',
            color: '#027BC6',
            textDecoration: 'underline',
            marginRight: '8px',
          }}>
            Date: {getDisplay()}
          </span>
          <img
            src={chevronDownIcon}
            alt=""
            style={{
              width: '12px',
              height: '12px',
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
              filter: BLUE_CHEVRON,
            }}
          />
        </Box>
        {startDate && endDate && (
          <button
            onClick={(e) => { e.stopPropagation(); handleReset(); }}
            style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '0 2px', display: 'flex', alignItems: 'center' }}
          >
            <img src={xIcon} alt="Réinitialiser" style={{ width: '14px', height: '14px', filter: BLUE_CHEVRON }} />
          </button>
        )}
      </Box>

      {isOpen && dropdownPosition && createPortal(
        <Box
          ref={dropdownRef}
          sx={{
            position: 'fixed',
            top: dropdownPosition.top,
            right: dropdownPosition.right,
            zIndex: 1500,
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden',
            border: '1px solid #E3E3E3',
            width: 'max-content',
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
            <DateCalendar
              value={startDate}
              onChange={handleDateSelection}
              slots={{ day: RangeDayPicker, calendarHeader: CustomCalendarHeader }}
              slotProps={{ day: { startDate, endDate } }}
              sx={{
                '& .MuiDayCalendar-weekDayLabel': {
                  fontFamily: 'Avenir',
                  fontWeight: 900,
                  fontSize: '10px',
                  lineHeight: '16px',
                  letterSpacing: '0.2px',
                  color: '#3E3E3E',
                  opacity: 0.5,
                  width: '40px !important',
                  minWidth: '40px !important',
                  flex: '0 0 40px !important',
                  textAlign: 'center !important',
                  margin: '0 2px !important',
                },
                '& .MuiDayCalendar-weekContainer': {
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                  gap: '4px',
                },
                '& .MuiPickersDay-root': {
                  width: '40px !important',
                  height: '40px !important',
                  margin: '0 !important',
                  display: 'flex !important',
                  alignItems: 'center !important',
                  justifyContent: 'center !important',
                  minWidth: '40px !important',
                  flex: '0 0 40px !important',
                },
              }}
            />
            <Box sx={{ p: 1 }}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <button
                  onClick={handleReset}
                  style={{ padding: '8px 16px', backgroundColor: 'transparent', color: '#919191', border: 'none', borderRadius: '12px', fontFamily: 'Montserrat', fontWeight: 600, fontSize: '14px', cursor: 'pointer', textDecoration: 'underline', height: '34px' }}
                >
                  Réinitialiser
                </button>
                <button
                  onClick={handleCancel}
                  style={{ padding: '8px 16px', backgroundColor: 'transparent', color: '#027BC6', border: 'none', borderRadius: '12px', fontFamily: 'Montserrat', fontWeight: 600, fontSize: '14px', cursor: 'pointer', height: '34px' }}
                >
                  Annuler
                </button>
                <button
                  onClick={handleApply}
                  style={{ padding: '8px 16px', backgroundColor: '#027BC6', color: 'white', border: 'none', borderRadius: '12px', fontFamily: 'Montserrat', fontWeight: 600, fontSize: '14px', cursor: 'pointer', width: '104px', height: '34px' }}
                  onMouseEnter={e => { e.target.style.backgroundColor = '#025a9c'; }}
                  onMouseLeave={e => { e.target.style.backgroundColor = '#027BC6'; }}
                >
                  Appliquer
                </button>
              </Box>
            </Box>
          </LocalizationProvider>
        </Box>,
        document.body
      )}
    </div>
  );
};

export default DateRangeFilter;
